import React, { useEffect, useMemo, useState } from "react";
import {
  isoDate,
  weekStartISO,
  getWeekNumber,
  fmtMinutes,
  prettifyStoreName,
  TIMEPRIS,
  tidsstyrtVerdi,
} from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const INVOICE_STORES = ["sport1royken", "sport1slemmestad"];

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

function fmtKr(amount) {
  return amount > 0 ? `${Math.round(amount).toLocaleString("nb-NO")} kr` : "—";
}

// Backend gir tallene per dag (dato = første gang servicen ble avsluttet), så
// hver måned får bare sine egne dager, også når en uke går over to måneder.
function getMonthForDate(dato) {
  return Number(dato.slice(5, 7));
}

function getYearForDate(dato) {
  return Number(dato.slice(0, 4));
}

function kortDato(dato) {
  return `${dato.slice(8, 10)}.${dato.slice(5, 7)}`;
}

function MonthDetailModal({ open, onClose, monthName, year, weekRows }) {
  if (!open) return null;

  const totals = weekRows.reduce(
    (acc, row) => {
      for (const slug of INVOICE_STORES) {
        const d = row.storeData[slug] || {};
        acc[slug].bruktMin += d.bruktMin || 0;
        acc[slug].omsetning += d.omsetning || 0;
        acc[slug].tidsstyrtMin += d.tidsstyrtMin || 0;
      }
      acc.totalMin += row.totalMin;
      acc.totalOmsetning += row.totalOmsetning;
      acc.totalTidsstyrtMin += row.totalTidsstyrtMin;
      return acc;
    },
    {
      sport1royken: { bruktMin: 0, omsetning: 0, tidsstyrtMin: 0 },
      sport1slemmestad: { bruktMin: 0, omsetning: 0, tidsstyrtMin: 0 },
      totalMin: 0,
      totalOmsetning: 0,
      totalTidsstyrtMin: 0,
    }
  );

  const hasTidsstyrt = totals.totalTidsstyrtMin > 0 ||
    weekRows.some(r => r.totalTidsstyrtMin > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold">{monthName} {year} – Ukeoversikt</h3>
            <div className="text-sm text-gray-500">{weekRows.length} uke{weekRows.length !== 1 ? "r" : ""}</div>
          </div>
          <button onClick={onClose} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100">
            Lukk
          </button>
        </div>

        <div className="overflow-auto max-h-[75vh] p-4 space-y-6">
          {/* Arbeidskost */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              Arbeidskost
            </h4>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Uke</th>
                  <th className="px-3 py-2 text-left">Dager</th>
                  {INVOICE_STORES.map((slug) => (
                    <React.Fragment key={slug}>
                      <th className="px-3 py-2 text-right">
                        {prettifyStoreName(slug)}<br />
                        <span className="font-normal text-gray-500">Timer</span>
                      </th>
                      <th className="px-3 py-2 text-right">
                        {prettifyStoreName(slug)}<br />
                        <span className="font-normal text-gray-500">Omsetning</span>
                      </th>
                    </React.Fragment>
                  ))}
                  <th className="px-3 py-2 text-right">Total timer</th>
                  <th className="px-3 py-2 text-right">Total omsetning</th>
                </tr>
              </thead>
              <tbody>
                {weekRows.map((row) => (
                  <tr key={row.weekIso} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">Uke {row.weekNo}</td>
                    <td className="px-3 py-2 text-gray-500">{row.periode}</td>
                    {INVOICE_STORES.map((slug) => {
                      const d = row.storeData[slug] || {};
                      const normalMin = (d.bruktMin || 0) - (d.tidsstyrtMin || 0);
                      return (
                        <React.Fragment key={slug}>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {normalMin > 0 ? fmtMinutes(normalMin) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {fmtKr(d.omsetning || 0)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-3 py-2 text-right text-gray-700">
                      {(row.totalMin - row.totalTidsstyrtMin) > 0
                        ? fmtMinutes(row.totalMin - row.totalTidsstyrtMin)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-bold">
                      {fmtKr(row.totalOmsetning)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                <tr>
                  <td colSpan={2} className="px-3 py-2">Total</td>
                  {INVOICE_STORES.map((slug) => {
                    const t = totals[slug];
                    return (
                      <React.Fragment key={slug}>
                        <td className="px-3 py-2 text-right">
                          {fmtMinutes((t.bruktMin || 0) - (t.tidsstyrtMin || 0))}
                        </td>
                        <td className="px-3 py-2 text-right">{fmtKr(t.omsetning)}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-3 py-2 text-right">
                    {fmtMinutes(totals.totalMin - totals.totalTidsstyrtMin)}
                  </td>
                  <td className="px-3 py-2 text-right">{fmtKr(totals.totalOmsetning)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Tidsstyrt arbeid */}
          {hasTidsstyrt && (
            <div>
              <h4 className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">
                Tidsstyrt arbeid – Reklamasjon, Gratisarbeid og Etterjustering ({TIMEPRIS} kr/t)
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-orange-50 text-orange-800">
                    <tr>
                      <th className="px-3 py-2 text-left">Uke</th>
                      <th className="px-3 py-2 text-left">Dager</th>
                      {INVOICE_STORES.map((slug) => (
                        <React.Fragment key={slug}>
                          <th className="px-3 py-2 text-right">
                            {prettifyStoreName(slug)}<br />
                            <span className="font-normal text-orange-600">Timer</span>
                          </th>
                          <th className="px-3 py-2 text-right">
                            {prettifyStoreName(slug)}<br />
                            <span className="font-normal text-orange-600">Verdi</span>
                          </th>
                        </React.Fragment>
                      ))}
                      <th className="px-3 py-2 text-right">Total timer</th>
                      <th className="px-3 py-2 text-right">Total verdi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekRows.map((row) => (
                      <tr key={row.weekIso} className="border-t hover:bg-orange-50">
                        <td className="px-3 py-2 font-medium">Uke {row.weekNo}</td>
                        <td className="px-3 py-2 text-gray-500">{row.periode}</td>
                        {INVOICE_STORES.map((slug) => {
                          const min = row.storeData[slug]?.tidsstyrtMin || 0;
                          return (
                            <React.Fragment key={slug}>
                              <td className="px-3 py-2 text-right text-orange-700">
                                {min > 0 ? fmtMinutes(min) : "—"}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-orange-800">
                                {min > 0 ? fmtKr(tidsstyrtVerdi(min)) : "—"}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className="px-3 py-2 text-right text-orange-700">
                          {row.totalTidsstyrtMin > 0 ? fmtMinutes(row.totalTidsstyrtMin) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-orange-800">
                          {row.totalTidsstyrtMin > 0 ? fmtKr(tidsstyrtVerdi(row.totalTidsstyrtMin)) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-orange-200 bg-orange-50 font-semibold">
                    <tr>
                      <td colSpan={2} className="px-3 py-2">Total</td>
                      {INVOICE_STORES.map((slug) => {
                        const min = totals[slug]?.tidsstyrtMin || 0;
                        return (
                          <React.Fragment key={slug}>
                            <td className="px-3 py-2 text-right">{fmtMinutes(min)}</td>
                            <td className="px-3 py-2 text-right">{fmtKr(tidsstyrtVerdi(min))}</td>
                          </React.Fragment>
                        );
                      })}
                      <td className="px-3 py-2 text-right">{fmtMinutes(totals.totalTidsstyrtMin)}</td>
                      <td className="px-3 py-2 text-right">{fmtKr(tidsstyrtVerdi(totals.totalTidsstyrtMin))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkshopMaanedsoversiktTab() {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const prevMonthYear = today.getMonth() === 0 ? currentYear - 1 : currentYear;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [dataByStore, setDataByStore] = useState({});
  const [modal, setModal] = useState({ open: false, monthName: "", year: 0, weekRows: [] });
  const [billingMonth, setBillingMonth] = useState(prevMonth);
  const [billingYear, setBillingYear] = useState(prevMonthYear);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2023; y--) years.push(y);
    return years;
  }, [currentYear]);

  const { fromISO, toISO } = useMemo(() => {
    const lastDayOfYear = new Date(selectedYear, 11, 31);
    const to = lastDayOfYear > today ? today : lastDayOfYear;
    return {
      fromISO: `${selectedYear}-01-01`,
      toISO: isoDate(to),
    };
  }, [selectedYear, today]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          INVOICE_STORES.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${fromISO}&to=${toISO}`
            ).then((r) => (r.ok ? r.json() : { perDag: [] }))
          )
        );

        const next = {};
        INVOICE_STORES.forEach((slug, i) => {
          next[slug] = responses[i].perDag || [];
        });
        setDataByStore(next);
      } catch (err) {
        console.error("Feil ved henting av månedsoversikt:", err);
        setDataByStore({});
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [fromISO, toISO]);

  const monthlyData = useMemo(() => {
    const maxMonth = selectedYear === currentYear ? currentMonth : 12;
    const months = [];

    for (let m = 1; m <= maxMonth; m++) {
      const storeData = {};
      let totalMin = 0;
      let totalOmsetning = 0;
      let totalTidsstyrtMin = 0;

      for (const slug of INVOICE_STORES) {
        const rows = dataByStore[slug] || [];
        let bruktMin = 0;
        let omsetning = 0;
        let tidsstyrtMin = 0;

        for (const row of rows) {
          if (getYearForDate(row.dato) === selectedYear && getMonthForDate(row.dato) === m) {
            bruktMin += Number(row.bruktMin || 0);
            omsetning += Number(row.omsetning || 0);
            tidsstyrtMin += Number(row.tidsstyrtMin || 0);
          }
        }

        storeData[slug] = { bruktMin, omsetning, tidsstyrtMin };
        totalMin += bruktMin;
        totalOmsetning += omsetning;
        totalTidsstyrtMin += tidsstyrtMin;
      }

      months.push({ month: m, monthName: MONTH_NAMES[m - 1], storeData, totalMin, totalOmsetning, totalTidsstyrtMin });
    }

    return months;
  }, [dataByStore, selectedYear, currentYear, currentMonth]);

  const yearTotals = useMemo(() => {
    const storeTotals = Object.fromEntries(
      INVOICE_STORES.map((s) => [s, { bruktMin: 0, omsetning: 0, tidsstyrtMin: 0 }])
    );
    let totalMin = 0;
    let totalOmsetning = 0;
    let totalTidsstyrtMin = 0;

    for (const row of monthlyData) {
      for (const slug of INVOICE_STORES) {
        storeTotals[slug].bruktMin += row.storeData[slug]?.bruktMin || 0;
        storeTotals[slug].omsetning += row.storeData[slug]?.omsetning || 0;
        storeTotals[slug].tidsstyrtMin += row.storeData[slug]?.tidsstyrtMin || 0;
      }
      totalMin += row.totalMin;
      totalOmsetning += row.totalOmsetning;
      totalTidsstyrtMin += row.totalTidsstyrtMin;
    }

    return { storeTotals, totalMin, totalOmsetning, totalTidsstyrtMin };
  }, [monthlyData]);

  const hasTidsstyrt = yearTotals.totalTidsstyrtMin > 0;

  const billingTotals = useMemo(() => {
    const storeTotals = Object.fromEntries(
      INVOICE_STORES.map((s) => [s, { omsetning: 0, tidsstyrtMin: 0 }])
    );
    let totalOmsetning = 0;
    let totalTidsstyrtMin = 0;

    for (const slug of INVOICE_STORES) {
      for (const row of dataByStore[slug] || []) {
        if (getYearForDate(row.dato) === billingYear && getMonthForDate(row.dato) === billingMonth) {
          storeTotals[slug].omsetning += Number(row.omsetning || 0);
          storeTotals[slug].tidsstyrtMin += Number(row.tidsstyrtMin || 0);
        }
      }
      totalOmsetning += storeTotals[slug].omsetning;
      totalTidsstyrtMin += storeTotals[slug].tidsstyrtMin;
    }

    return { storeTotals, totalOmsetning, totalTidsstyrtMin };
  }, [dataByStore, billingMonth, billingYear]);

  function openMonthModal(monthRow) {
    // Dagene i måneden, gruppert per uke. Uker som går over to måneder vises bare
    // med dagene som hører til denne måneden.
    const perUke = {};
    for (const slug of INVOICE_STORES) {
      for (const row of dataByStore[slug] || []) {
        if (getYearForDate(row.dato) !== selectedYear || getMonthForDate(row.dato) !== monthRow.month) continue;
        const weekIso = weekStartISO(new Date(row.dato + "T00:00:00"));
        const uke = (perUke[weekIso] ||= {
          weekIso,
          weekNo: getWeekNumber(new Date(weekIso + "T00:00:00")),
          fra: row.dato,
          til: row.dato,
          storeData: Object.fromEntries(
            INVOICE_STORES.map((s) => [s, { bruktMin: 0, omsetning: 0, tidsstyrtMin: 0 }])
          ),
          totalMin: 0,
          totalOmsetning: 0,
          totalTidsstyrtMin: 0,
        });
        if (row.dato < uke.fra) uke.fra = row.dato;
        if (row.dato > uke.til) uke.til = row.dato;
        const d = uke.storeData[slug];
        d.bruktMin += Number(row.bruktMin || 0);
        d.omsetning += Number(row.omsetning || 0);
        d.tidsstyrtMin += Number(row.tidsstyrtMin || 0);
        uke.totalMin += Number(row.bruktMin || 0);
        uke.totalOmsetning += Number(row.omsetning || 0);
        uke.totalTidsstyrtMin += Number(row.tidsstyrtMin || 0);
      }
    }

    const weekRows = Object.values(perUke)
      .sort((a, b) => a.weekIso.localeCompare(b.weekIso))
      .map((uke) => ({
        ...uke,
        periode: uke.fra === uke.til ? kortDato(uke.fra) : `${kortDato(uke.fra)}–${kortDato(uke.til)}`,
      }));

    setModal({
      open: true,
      monthName: monthRow.monthName,
      year: selectedYear,
      weekRows,
    });
  }

  if (loading) {
    return <div className="py-10 text-center">Laster månedsoversikt…</div>;
  }

  return (
    <div className="space-y-6">
      <MonthDetailModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        monthName={modal.monthName}
        year={modal.year}
        weekRows={modal.weekRows}
      />

      <div className="rounded border bg-blue-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xl font-bold">Faktureringsgrunnlag – Arbeid</div>
            <div className="text-sm text-gray-700 mt-1">
              Timer og arbeidsinntekt per måned for Sport 1 Røyken og Sport 1 Slemmestad. Arbeidet telles i måneden servicen ble avsluttet. Klikk på en måned for ukeoversikt.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">År:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sammendragskort */}
      <div className="grid md:grid-cols-3 gap-4">
        {INVOICE_STORES.map((slug) => {
          const t = yearTotals.storeTotals[slug];
          const normalMin = (t?.bruktMin || 0) - (t?.tidsstyrtMin || 0);
          return (
            <div key={slug} className="rounded border p-4 bg-white">
              <div className="text-sm text-gray-600">{prettifyStoreName(slug)}</div>
              <div className="text-xl font-bold mt-1">{fmtKr(t?.omsetning || 0)}</div>
              <div className="text-sm text-gray-500">{fmtMinutes(normalMin)} arbeidskost</div>
              {(t?.tidsstyrtMin || 0) > 0 && (
                <div className="text-sm text-orange-600 mt-0.5">
                  + {fmtMinutes(t.tidsstyrtMin)} tidsstyrt ({fmtKr(tidsstyrtVerdi(t.tidsstyrtMin))})
                </div>
              )}
            </div>
          );
        })}
        <div className="rounded border p-4 bg-blue-100">
          <div className="text-sm text-gray-600">Totalt {selectedYear}</div>
          <div className="text-xl font-bold mt-1">{fmtKr(yearTotals.totalOmsetning)}</div>
          <div className="text-sm text-gray-500">
            {fmtMinutes(yearTotals.totalMin - yearTotals.totalTidsstyrtMin)} arbeidskost
          </div>
          {yearTotals.totalTidsstyrtMin > 0 && (
            <div className="text-sm text-orange-600 mt-0.5">
              + {fmtMinutes(yearTotals.totalTidsstyrtMin)} tidsstyrt ({fmtKr(tidsstyrtVerdi(yearTotals.totalTidsstyrtMin))})
            </div>
          )}
        </div>
      </div>

      {/* Arbeidskost-tabell */}
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-2">Arbeidskost</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded bg-white">
            <thead className="bg-gray-100 text-sm text-gray-700">
              <tr>
                <th className="px-4 py-2 text-left">Måned</th>
                {INVOICE_STORES.map((slug) => (
                  <React.Fragment key={slug}>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-gray-500">Timer</span>
                    </th>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-gray-500">Omsetning</span>
                    </th>
                  </React.Fragment>
                ))}
                <th className="px-4 py-2 text-right">Total timer</th>
                <th className="px-4 py-2 text-right">Total omsetning</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((row) => {
                const normalMin = row.totalMin - row.totalTidsstyrtMin;
                return (
                  <tr
                    key={row.month}
                    className="border-t cursor-pointer hover:bg-blue-50"
                    onClick={() => openMonthModal(row)}
                    title="Klikk for ukeoversikt"
                  >
                    <td className="px-4 py-2 font-medium text-blue-700 underline underline-offset-2">
                      {row.monthName} {selectedYear}
                    </td>
                    {INVOICE_STORES.map((slug) => {
                      const d = row.storeData[slug] || {};
                      const storeNormalMin = (d.bruktMin || 0) - (d.tidsstyrtMin || 0);
                      return (
                        <React.Fragment key={slug}>
                          <td className="px-4 py-2 text-right text-gray-700">
                            {storeNormalMin > 0 ? fmtMinutes(storeNormalMin) : "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            {fmtKr(d.omsetning || 0)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-4 py-2 text-right text-gray-700">
                      {normalMin > 0 ? fmtMinutes(normalMin) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-bold">
                      {fmtKr(row.totalOmsetning)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-sm">
              <tr>
                <td className="px-4 py-2">Total {selectedYear}</td>
                {INVOICE_STORES.map((slug) => {
                  const t = yearTotals.storeTotals[slug];
                  return (
                    <React.Fragment key={slug}>
                      <td className="px-4 py-2 text-right">
                        {fmtMinutes((t?.bruktMin || 0) - (t?.tidsstyrtMin || 0))}
                      </td>
                      <td className="px-4 py-2 text-right">{fmtKr(t?.omsetning || 0)}</td>
                    </React.Fragment>
                  );
                })}
                <td className="px-4 py-2 text-right">
                  {fmtMinutes(yearTotals.totalMin - yearTotals.totalTidsstyrtMin)}
                </td>
                <td className="px-4 py-2 text-right">{fmtKr(yearTotals.totalOmsetning)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Tidsstyrt arbeid-tabell */}
      <div>
        <h3 className="text-base font-semibold text-orange-700 mb-1">
          Tidsstyrt arbeid – Reklamasjon, Gratisarbeid og Etterjustering
        </h3>
        <p className="text-sm text-gray-500 mb-2">
          Registreres ikke som arbeidskost. Faktureres etter tid til fast sats {TIMEPRIS} kr/t.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded bg-white">
            <thead className="bg-orange-50 text-sm text-orange-800">
              <tr>
                <th className="px-4 py-2 text-left">Måned</th>
                {INVOICE_STORES.map((slug) => (
                  <React.Fragment key={slug}>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-orange-600">Timer</span>
                    </th>
                    <th className="px-4 py-2 text-right">
                      {prettifyStoreName(slug)}<br />
                      <span className="font-normal text-orange-600">Verdi</span>
                    </th>
                  </React.Fragment>
                ))}
                <th className="px-4 py-2 text-right">Total timer</th>
                <th className="px-4 py-2 text-right">Total verdi</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((row) => (
                <tr
                  key={row.month}
                  className="border-t cursor-pointer hover:bg-orange-50"
                  onClick={() => openMonthModal(row)}
                  title="Klikk for ukeoversikt"
                >
                  <td className="px-4 py-2 font-medium text-blue-700 underline underline-offset-2">
                    {row.monthName} {selectedYear}
                  </td>
                  {INVOICE_STORES.map((slug) => {
                    const min = row.storeData[slug]?.tidsstyrtMin || 0;
                    return (
                      <React.Fragment key={slug}>
                        <td className="px-4 py-2 text-right text-orange-700">
                          {min > 0 ? fmtMinutes(min) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-orange-800">
                          {min > 0 ? fmtKr(tidsstyrtVerdi(min)) : "—"}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-4 py-2 text-right text-orange-700">
                    {row.totalTidsstyrtMin > 0 ? fmtMinutes(row.totalTidsstyrtMin) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-bold text-orange-800">
                    {row.totalTidsstyrtMin > 0 ? fmtKr(tidsstyrtVerdi(row.totalTidsstyrtMin)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-orange-200 bg-orange-50 font-semibold text-sm">
              <tr>
                <td className="px-4 py-2">Total {selectedYear}</td>
                {INVOICE_STORES.map((slug) => {
                  const min = yearTotals.storeTotals[slug]?.tidsstyrtMin || 0;
                  return (
                    <React.Fragment key={slug}>
                      <td className="px-4 py-2 text-right">{min > 0 ? fmtMinutes(min) : "—"}</td>
                      <td className="px-4 py-2 text-right">{min > 0 ? fmtKr(tidsstyrtVerdi(min)) : "—"}</td>
                    </React.Fragment>
                  );
                })}
                <td className="px-4 py-2 text-right">
                  {yearTotals.totalTidsstyrtMin > 0 ? fmtMinutes(yearTotals.totalTidsstyrtMin) : "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  {yearTotals.totalTidsstyrtMin > 0 ? fmtKr(tidsstyrtVerdi(yearTotals.totalTidsstyrtMin)) : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {!hasTidsstyrt && (
          <div className="mt-2 text-sm text-gray-400 italic">
            Ingen tidsstyrt arbeid registrert i {selectedYear}.
          </div>
        )}
      </div>

      {/* Samlet faktureringsgrunnlag */}
      <div className="rounded border-2 border-blue-300 bg-blue-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="text-base font-bold text-blue-900">
            Faktureringsgrunnlag – {MONTH_NAMES[billingMonth - 1]} {billingYear}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Måned:</label>
            <select
              value={billingMonth}
              onChange={(e) => setBillingMonth(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm bg-white"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={billingYear}
              onChange={(e) => setBillingYear(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-600 border-b border-blue-200">
              <tr>
                <th className="pb-2 text-left font-medium">Butikk</th>
                <th className="pb-2 text-right font-medium">Arbeidskost</th>
                <th className="pb-2 text-right font-medium">Tidsstyrt ({TIMEPRIS} kr/t)</th>
                <th className="pb-2 text-right font-semibold">Total å fakturere</th>
              </tr>
            </thead>
            <tbody>
              {INVOICE_STORES.map((slug) => {
                const t = billingTotals.storeTotals[slug];
                const arbeid = t?.omsetning || 0;
                const tidsstyrt = tidsstyrtVerdi(t?.tidsstyrtMin || 0);
                return (
                  <tr key={slug} className="border-t border-blue-200">
                    <td className="py-2 font-medium">{prettifyStoreName(slug)}</td>
                    <td className="py-2 text-right">{fmtKr(arbeid)}</td>
                    <td className="py-2 text-right text-orange-700">
                      {(t?.tidsstyrtMin || 0) > 0 ? fmtKr(tidsstyrt) : "—"}
                    </td>
                    <td className="py-2 text-right font-bold text-blue-900">{fmtKr(arbeid + tidsstyrt)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-blue-400">
              <tr>
                <td className="pt-2 font-bold">Total</td>
                <td className="pt-2 text-right font-bold">{fmtKr(billingTotals.totalOmsetning)}</td>
                <td className="pt-2 text-right font-bold text-orange-700">
                  {billingTotals.totalTidsstyrtMin > 0
                    ? fmtKr(tidsstyrtVerdi(billingTotals.totalTidsstyrtMin))
                    : "—"}
                </td>
                <td className="pt-2 text-right font-bold text-lg text-blue-900">
                  {fmtKr(billingTotals.totalOmsetning + tidsstyrtVerdi(billingTotals.totalTidsstyrtMin))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
